import React from 'react';
import { CompositeReportResult, Page, NavParams } from '@types';
import { md, Disclaimer, ICONS, normalizeRiskScore, formatTimestamp } from './ReportShared';

interface CompositeReportDisplayProps {
    result: CompositeReportResult;
    onNavigate?: (nav: { page: Page; params?: NavParams }) => void;
}

const CompositeReportDisplay: React.FC<CompositeReportDisplayProps> = ({ result, onNavigate }) => {
    // Determine if this is an operational brief or intelligence report
    const isOperational = (result as any).analysisType === 'operational_analytics' || !result.sections;

    return (
        <div className="space-y-8 animate-fade-in print:space-y-4">
            {/* Executive Header */}
            <header className="border-b-4 border-slate-900 pb-6 mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div className="space-y-1">
                    <div className="text-xs font-bold tracking-widest text-slate-500 uppercase flex items-center gap-2">
                        <span className="w-8 h-[2px] bg-indigo-600"></span>
                        {isOperational ? 'Operational Analytics Brief' : 'Executive Intelligence Report'}
                    </div>
                    <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight leading-none">{result.reportTitle}</h1>
                    <p className="text-lg text-slate-500 font-medium">IntelOptics Strategic Reporting Hub</p>
                </div>
                <div className="text-right flex flex-col items-end gap-2">
                    <div className={`px-4 py-1 rounded text-xs font-black tracking-widest uppercase border-2 ${
                        result.classification === 'TOP SECRET' ? 'bg-red-950 text-red-400 border-red-800' :
                        result.classification === 'SECRET' ? 'bg-orange-950 text-orange-400 border-orange-800' :
                        'bg-slate-900 text-slate-200 border-slate-700'
                    }`}>
                        {result.classification}
                    </div>
                    <div className="text-[10px] font-mono text-slate-400 leading-tight">
                        ID: {result.reportId || 'PENDING'}<br/>
                        VER: {result.metadata?.version || '1.0.0'}<br/>
                        GEN: {formatTimestamp(result.metadata?.generatedAt)}
                    </div>
                </div>
            </header>

            {/* Strategic Overview Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    {/* Operational Brief Content Fallback */}
                    {isOperational && (result as any).executiveSummary && (
                        <div className="bg-white border-l-4 border-indigo-600 shadow-sm rounded-r-lg overflow-hidden p-8">
                            <h2 className="text-xl font-bold text-slate-900 mb-6 uppercase tracking-wider flex items-center gap-2">
                                <span className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">{ICONS.shield}</span>
                                Operational Executive Summary
                            </h2>
                            <div 
                                className="prose prose-slate max-w-none text-slate-700 leading-relaxed" 
                                dangerouslySetInnerHTML={{ __html: md.render((result as any).executiveSummary) }} 
                            />
                        </div>
                    )}

                    {/* Risk Analysis Fallback for Operational */}
                    {isOperational && (result as any).riskAnalysis && (
                        <div className="bg-white border-l-4 border-amber-500 shadow-sm rounded-r-lg overflow-hidden p-8">
                            <h2 className="text-xl font-bold text-slate-900 mb-6 uppercase tracking-wider flex items-center gap-2">
                                <span className="p-2 bg-amber-50 text-amber-500 rounded-lg">{ICONS.alert}</span>
                                Strategic Risk Assessment
                            </h2>
                            <div 
                                className="prose prose-slate max-w-none text-slate-700 leading-relaxed" 
                                dangerouslySetInnerHTML={{ __html: md.render((result as any).riskAnalysis) }} 
                            />
                        </div>
                    )}

                    {/* Intelligence Sections (Standard View) */}
                    {!isOperational && result.sections && result.sections.map((section: any, idx: number) => (
                        <div key={idx} className="bg-white border-l-4 border-indigo-600 shadow-sm rounded-r-lg overflow-hidden group hover:shadow-md transition-shadow">
                            <div className="bg-slate-50 px-6 py-3 border-b border-slate-100 flex items-center justify-between">
                                <h3 className="font-bold text-slate-800 uppercase tracking-wide flex items-center gap-2">
                                    <span className="text-indigo-600 text-lg font-mono">0{idx + 1}</span>
                                    {section.title}
                                </h3>
                                <div className="text-[10px] font-bold text-slate-400 bg-slate-200/50 px-2 py-0.5 rounded">
                                    {section.type.replace(/_/g, ' ').toUpperCase()}
                                </div>
                            </div>
                            <div className="p-6 prose prose-slate prose-sm max-w-none">
                                {section.result?.summary ? (
                                    <div dangerouslySetInnerHTML={{ __html: md.render(section.result.summary) }} />
                                ) : section.result?.executiveSummary ? (
                                    <div dangerouslySetInnerHTML={{ __html: md.render(section.result.executiveSummary) }} />
                                ) : (
                                    <p className="text-slate-500 italic">Analysis component processing complete. View detailed annex for full data structure.</p>
                                )}
                            </div>
                        </div>
                    ))}
                    {/* Additional Intelligence Annex */}
                    {result.additionalSections && result.additionalSections.length > 0 && (
                        <div className="space-y-6 mt-12 pt-12 border-t border-slate-200">
                            <h2 className="text-xl font-bold text-slate-900 mb-6 uppercase tracking-wider flex items-center gap-2">
                                <span className="p-2 bg-slate-100 text-slate-600 rounded-lg">{ICONS.activity}</span>
                                Intelligence Annex Data
                            </h2>
                            {result.additionalSections.map((section: any, idx: number) => (
                                <div key={idx} className="bg-white border-l-4 border-slate-400 shadow-sm rounded-r-lg overflow-hidden p-6">
                                    <h3 className="font-bold text-slate-800 uppercase tracking-wide mb-4">{section.title}</h3>
                                    <div className="prose prose-slate prose-sm max-w-none text-slate-600" dangerouslySetInnerHTML={{ __html: md.render(section.content || '') }} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="space-y-6">
                    {/* Visual Analytics Trigger */}
                    <div className="bg-slate-900 text-white rounded-xl p-6 shadow-xl border border-slate-800 text-center">
                        <div className="mb-4 inline-flex p-3 bg-indigo-500/20 rounded-full text-indigo-400">
                            {ICONS.chart}
                        </div>
                        <h3 className="text-lg font-bold mb-2">High-Fidelity Dashboard</h3>
                        <p className="text-xs text-slate-400 mb-6 leading-relaxed">This report is optimized for the IntelOptics Executive Viewer with dynamic data visualizations.</p>
                        <button 
                            onClick={() => onNavigate?.({ page: 'executiveReport', params: { reportId: result.reportId, directData: result } })}
                            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2 rounded font-bold text-xs uppercase tracking-widest transition-all"
                        >
                            Launch High-Fidelity Viewer
                        </button>
                    </div>

                    {/* Operational KPIs (If available) */}
                    {isOperational && (result as any).stats && (
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 shadow-sm">
                            <h4 className="font-bold text-slate-800 text-sm mb-4 uppercase tracking-widest flex items-center gap-2">
                                <span className="w-2 h-2 bg-indigo-600 rounded-full"></span>
                                Key Performance Metrics
                            </h4>
                            <div className="space-y-4">
                                {Object.entries((result as any).stats).map(([label, value]: [string, any]) => (
                                    <div key={label} className="flex justify-between items-center py-2 border-b border-slate-200 last:border-0">
                                        <span className="text-xs text-slate-500 font-medium capitalize">{label.replace(/([A-Z])/g, ' $1').trim()}</span>
                                        <span className="text-sm font-bold text-slate-900">{value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Intelligence Distribution View */}
                    {!isOperational && result.sections && (
                        <div className="bg-indigo-900 border border-indigo-500/30 rounded-xl p-5 text-indigo-100 shadow-lg relative overflow-hidden group">
                            <h4 className="font-bold text-lg mb-4 relative z-10">Risk Distribution</h4>
                            <div className="space-y-3 relative z-10">
                                {result.sections.map((s: any, i: number) => {
                                    const rawRisk = (s.result as any).overallGeopoliticalRiskScore || (s.result as any).riskScore || 20;
                                    const risk = normalizeRiskScore(rawRisk);
                                    return (
                                        <div key={i} className="space-y-1">
                                            <div className="flex justify-between text-[10px] font-bold uppercase tracking-wide">
                                                <span>{s.title}</span>
                                                <span>LEVEL {risk}</span>
                                            </div>
                                            <div className="h-1.5 bg-indigo-950 rounded-full overflow-hidden">
                                                <div 
                                                    className={`h-full rounded-full transition-all duration-1000 ${
                                                        risk >= 80 ? 'bg-red-500' : risk >= 50 ? 'bg-orange-500' : 'bg-emerald-500'
                                                    }`}
                                                    style={{ width: `${risk}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <Disclaimer />
            
            <div className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-xl p-6 text-center">
                 <h4 className="font-bold text-slate-800 mb-1">Operational Integration Ready</h4>
                 <p className="text-sm text-slate-600 mb-4">This data represents a verified snapshot of the organizational security posture at the time of generation.</p>
                 <div className="flex flex-wrap justify-center gap-3">
                    <button 
                        onClick={() => onNavigate?.({ page: 'rgs' })}
                        className="bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-black transition-colors"
                    >
                        Return to Hub
                    </button>
                 </div>
            </div>
        </div>
    );
};

export default CompositeReportDisplay;
